import React from 'react';

import * as api from '@gitbook/api-client';
import {
    Button,
    FieldGroup,
    Heading,
    Panel,
    PanelBody,
    type TileOption,
    TilePicker,
} from '@gitbook/spine-react';
import { useStateWithDeps } from '@gitbook/util-react-hooks';

import { PaidFeatureIndicator } from '../../../../../Components';
import { useSiteContext, useUpgradeSite } from '../../../../../state';
import { useSiteSettingsContext } from '../SiteSettingsContext';
import assistant from './assets/assistant.svg';
import assistantDark from './assets/assistant-dark.svg';
import none from './assets/none.svg';
import noneDark from './assets/none-dark.svg';
import search from './assets/search.svg';
import searchDark from './assets/search-dark.svg';

export function SitePickAIModePanel() {
    const { site } = useSiteContext();
    const { siteSettings, updateSiteSettings, savingSiteSettings } = useSiteSettingsContext();

    const initialMode = siteSettings.ai.mode;
    const [mode, setMode] = useStateWithDeps(initialMode, [initialMode]);

    const upgradeForSearch = useUpgradeSite(site, api.SiteFeatureKey.SitesAiSearch);
    const upgradeForAssistant = useUpgradeSite(site, api.SiteFeatureKey.SitesAiAssistant);

    const options = React.useMemo<TileOption<api.CustomizationAIMode>[]>(() => {
        return [
            {
                value: api.CustomizationAIMode.None,
                label: 'None',
                description: 'No AI experience will be available to your visitors.',
                asset: {
                    light: none,
                    dark: noneDark,
                },
            },
            {
                value: api.CustomizationAIMode.Search,
                label: 'Search',
                description: 'Short answers to user questions directly from the search box.',
                asset: {
                    light: search,
                    dark: searchDark,
                },
                disabled: !!upgradeForSearch,
                trailing: (
                    <PaidFeatureIndicator
                        upgrade={upgradeForSearch}
                        isFeatureActive={initialMode === api.CustomizationAIMode.Search}
                    />
                ),
            },
            {
                value: api.CustomizationAIMode.Assistant,
                label: 'Assistant',
                description: 'An advanced interactive chat experience with our AI agent.',
                asset: {
                    light: assistant,
                    dark: assistantDark,
                },
                disabled: !!upgradeForAssistant,
                trailing: (
                    <PaidFeatureIndicator
                        upgrade={upgradeForAssistant}
                        isFeatureActive={initialMode === api.CustomizationAIMode.Assistant}
                    />
                ),
            },
        ];
    }, [initialMode, upgradeForAssistant, upgradeForSearch]);

    return (
        <Panel>
            <Heading level={2}>Choose the AI experience</Heading>
            <PanelBody>
                <FieldGroup orientation="vertical">
                    <TilePicker
                        disabled={savingSiteSettings}
                        items={options}
                        selectedItem={mode}
                        setSelectedItem={setMode}
                    />
                    {mode !== initialMode ? (
                        <div className="mt-3 flex justify-end gap-2 border-t pt-3">
                            <Button
                                label="Cancel"
                                kind="blank"
                                onPress={() => {
                                    setMode(initialMode);
                                }}
                            />
                            <Button
                                label="Save"
                                onPress={async () => {
                                    await updateSiteSettings(
                                        {
                                            ai: {
                                                mode,
                                            },
                                        },
                                        { title: 'AI settings saved' }
                                    );
                                }}
                            />
                        </div>
                    ) : null}
                </FieldGroup>
            </PanelBody>
        </Panel>
    );
}
